import qs from 'qs';

import type { actions } from '@luxuryescapes/contract-svc-contracting';

import type {
  ContractingCaseDetail,
  ContractingCaseFieldHistoryResponse,
  ContractingDealFieldHistoryEntityType,
  ContractingFieldHistoryParams,
  ContractingFieldHistoryResponse,
  ContractingQueueCasesParams,
  ContractingQueueCasesResponse,
} from '~/types/services/contracting';

import { EXPECTED_NOT_FOUND, RequestOptions, json_headers, request } from './common';

const BASE_URL = `${window.configs.API_HOST}/api/contracting/v1`;

type ProjectTemplatesResponse =
  actions['getProjectTemplates']['responses'][200]['content']['application/json']['result'];
type ProjectResponse = NonNullable<
  actions['getOpportunityProject']['responses'][200]['content']['application/json']['result']
>;
type ProjectTaskResponse = ProjectResponse['tasks'][number];
type ReplaceProjectBody = actions['replaceOpportunityProject']['requestBody']['content']['application/json'];
type UpdateProjectTaskBody = actions['updateProjectTask']['requestBody']['content']['application/json'];
export type AccountDetailsResponse =
  actions['getAccountDetails']['responses'][200]['content']['application/json']['result'];

export type ProjectTaskStatus = ProjectTaskResponse['status'];
export type ProjectAssignee = ProjectTaskResponse['assignees'][number];

export interface ProjectTask {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  status: ProjectTaskStatus;
  assignees: Array<ProjectAssignee>;
}

export interface Project {
  id: string;
  name: string;
  templateName: string;
  estimatedHours: number;
  startDate: string;
  endDate: string;
  tasks: Array<ProjectTask>;
}

export interface ProjectTemplate {
  id: string;
  name: string;
  taskNames: Array<string>;
}

export interface NewProjectValues {
  templateId: string;
  estimatedHours: number;
  startDate: string;
  endDate: string;
}

export interface ProjectTaskChanges {
  status?: ProjectTaskStatus;
  beforeTaskId?: string;
  assignees?: Array<ProjectAssignee>;
}

function toProjectTask(task: ProjectTaskResponse): ProjectTask {
  return {
    id: task.id,
    name: task.name,
    startDate: task.start_date,
    endDate: task.end_date,
    status: task.status,
    assignees: task.assignees,
  };
}

function toProject(project: ProjectResponse): Project {
  return {
    id: project.id,
    name: project.name,
    templateName: project.template_name,
    estimatedHours: project.estimated_hours,
    startDate: project.start_date,
    endDate: project.end_date,
    tasks: project.tasks.map(toProjectTask),
  };
}

function toProjectTemplate(template: ProjectTemplatesResponse[number]): ProjectTemplate {
  return { id: template.id, name: template.name, taskNames: template.task_names };
}

export async function getProjectTemplates({ signal }: RequestOptions = {}): Promise<Array<ProjectTemplate>> {
  const response = await request(`${BASE_URL}/project-templates`, { method: 'GET', signal });
  return (response.result as ProjectTemplatesResponse).map(toProjectTemplate);
}

export async function getOpportunityProject(
  opportunityId: string,
  { signal }: RequestOptions = {},
): Promise<Project | null> {
  const response = await request(`${BASE_URL}/opportunities/${opportunityId}/project`, { method: 'GET', signal });
  const project = response.result as ProjectResponse | null;
  return project && toProject(project);
}

export async function createOpportunityProject(
  opportunityId: string,
  opportunityName: string,
  { templateId, estimatedHours, startDate, endDate }: NewProjectValues,
): Promise<Project> {
  const body: ReplaceProjectBody = {
    template_id: templateId,
    opportunity_name: opportunityName,
    estimated_hours: estimatedHours,
    start_date: startDate,
    end_date: endDate,
  };
  const response = await request(`${BASE_URL}/opportunities/${opportunityId}/project`, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify(body),
  });
  return toProject(response.result as ProjectResponse);
}

export async function updateProjectTask(
  projectId: string,
  taskId: string,
  { status, beforeTaskId, assignees }: ProjectTaskChanges,
): Promise<Project> {
  const body: UpdateProjectTaskBody = {
    status,
    before_task_id: beforeTaskId,
    assignees,
  };
  const response = await request(`${BASE_URL}/projects/${projectId}/tasks/${taskId}`, {
    method: 'PATCH',
    headers: json_headers,
    body: JSON.stringify(body),
  });
  return toProject(response.result as ProjectResponse);
}

function toFieldHistoryQuery(
  { offset, limit, sortOrder }: ContractingFieldHistoryParams,
  extra: { entity_type?: ContractingDealFieldHistoryEntityType } = {},
) {
  return qs.stringify({ offset, limit, sort_order: sortOrder, ...extra }, { skipNulls: true });
}

export async function getOpportunityFieldHistory(
  salesforceOpportunityId: string,
  entityType: ContractingDealFieldHistoryEntityType,
  params: ContractingFieldHistoryParams,
  { signal }: RequestOptions = {},
): Promise<ContractingFieldHistoryResponse> {
  const id = encodeURIComponent(salesforceOpportunityId);
  const query = toFieldHistoryQuery(params, { entity_type: entityType });
  return request(`${BASE_URL}/opportunities/by-external-ref/salesforce/${id}/field-history?${query}`, { signal });
}

export async function getAccountFieldHistory(
  salesforceAccountId: string,
  params: ContractingFieldHistoryParams,
  { signal }: RequestOptions = {},
): Promise<ContractingFieldHistoryResponse> {
  const id = encodeURIComponent(salesforceAccountId);
  const query = toFieldHistoryQuery(params);
  return request(`${BASE_URL}/accounts/by-external-ref/salesforce/${id}/field-history?${query}`, { signal });
}

export async function getAccountIdBySalesforceId(
  salesforceAccountId: string,
  { signal }: RequestOptions = {},
): Promise<string> {
  const id = encodeURIComponent(salesforceAccountId);
  const { result } = await request(`${BASE_URL}/accounts/by-external-ref/salesforce/${id}`, { signal });
  return result.id;
}

export async function getAccountDetails(
  accountId: string,
  { signal }: RequestOptions = {},
): Promise<AccountDetailsResponse> {
  const { result } = await request(`${BASE_URL}/accounts/${accountId}/details`, { signal });
  return result;
}

export async function getCaseFieldHistory(
  caseNumber: string,
  params: ContractingFieldHistoryParams,
  { signal }: RequestOptions = {},
): Promise<ContractingCaseFieldHistoryResponse> {
  const query = toFieldHistoryQuery(params);
  return request(
    `${BASE_URL}/cases/by-external-ref/salesforce/${encodeURIComponent(caseNumber)}/field-history?${query}`,
    // A customer case or mistyped number is a 404 the case page explains.
    { signal, ...EXPECTED_NOT_FOUND },
  );
}

export async function getTeamQueueCases(
  teamKey: string,
  { status, offset, limit }: ContractingQueueCasesParams,
  { signal }: RequestOptions = {},
): Promise<ContractingQueueCasesResponse> {
  const query = qs.stringify({ status, offset, limit }, { skipNulls: true });
  return request(`${BASE_URL}/teams/${encodeURIComponent(teamKey)}/cases?${query}`, { signal });
}

export async function getCase(caseNumber: string, { signal }: RequestOptions = {}): Promise<ContractingCaseDetail> {
  const { result } = await request(`${BASE_URL}/cases/by-external-ref/salesforce/${encodeURIComponent(caseNumber)}`, {
    signal,
    ...EXPECTED_NOT_FOUND,
  });
  return result;
}
